/**
 * Public IP API Service
 *
 * Service para gerenciamento de IPs públicos (IPv4).
 * CP: gerencia pools de IPs públicos.
 * EC: compra, lista e libera IPs públicos.
 */

import type { PublicIPHistoryResponse, PublicIPRemovedListResponse } from '../utils/publicIpHistory'
import type {
  PublicIPAllocatePreview,
  PublicIPAllocateRequest,
  PublicIPAllocationTargets,
  PublicIPPoolEntry,
  PublicIPRefundPreview,
  PublicIPReleasePreview,
  PublicIPReleaseRequest,
} from '../utils/publicIpPool'
import { apiClient } from './api'

// ===== Types =====

/**
 * A linha da listagem do pool. Desde a #1059 traz de quem é o IP (revenda, comprador),
 * onde está (VM com status, ou a WAN de um Cloud Gateway) e o estado de cobrança
 * (`billing_mode`, `next_billing_date`) — o que o CP precisa para julgar uma alocação.
 */
export interface PublicIP extends PublicIPPoolEntry {
  gateway: string
  netmask: string
  dns_servers?: string[]
  network_pool_id: string
  cluster_id: string
  product_id?: string | null
  contract_id?: string | null
  status: 'available' | 'allocated' | 'attached' | 'released'
  released_at?: string | null
  created_at: string
  updated_at: string
}

export interface PublicIPReleaseResponse {
  ip_id: string
  ip_address: string
  status: string
  estorno: PublicIPRefundPreview
  message: string
}

export interface PublicIPAllocateResponse extends PublicIPAllocatePreview {
  ip_id: string
  ip_address: string
  status: string
  message: string
}

export interface PublicIPListResponse {
  total: number
  items: PublicIP[]
}

export interface PublicIPRangeCreateRequest {
  cidr: string
  gateway: string
  netmask: string
  dns_servers?: string[]
  start_ip?: string
  end_ip?: string
}

export interface PublicIPRangeCreateResponse {
  created_count: number
  skipped_count: number
  items: PublicIP[]
  errors: string[]
}

export interface PublicIPAvailable {
  id: string
  ip_address: string
  cluster_id: string
  cluster_name?: string
  product_id?: string
  product_name?: string
  base_price?: number
  final_price?: number
  billing_cycle?: string
}

export interface PublicIPAvailableListResponse {
  total: number
  items: PublicIPAvailable[]
}

export interface PublicIPPurchaseRequest {
  product_id: string
  payment_option: 'credit_card' | 'credit_account'
  payment_method_id?: string
  reseller_id?: string
  /**
   * Cluster onde o IP sera usado. Sem ele o backend escolhe; num provedor com
   * mais de um cluster com estoque a compra e recusada (409) em vez de chutar,
   * porque a VM so aceita anexar IP do proprio cluster (issue #691).
   */
  cluster_id?: string
}

export interface PublicIPPurchaseResponse {
  ip_id: string
  ip_address: string
  status: string
  unit_price: number
  billing_cycle: string
  contract_id: string
  message: string
}

export interface PublicIPCustomer {
  id: string
  ip_address: string
  gateway: string
  netmask: string
  dns_servers?: string[]
  status: string
  cluster_id: string
  cluster_name?: string
  vm_instance_id?: string
  vm_instance_name?: string
  /** O outro uso possivel do endereco: a WAN de um Cloud Gateway (issue #291).
   *  Sem ele a coluna de uso mostrava "-" para um IP que o backend recusa liberar,
   *  e o cliente lia isso como "nao esta vinculado a nada". */
  cloud_gateway_id?: string
  cloud_gateway_name?: string
  unit_price?: number
  billing_cycle?: string
  /** Vencimento do ciclo corrente. Ausente = fora da cobranca recorrente
   *  (posse anterior a #826, ou compra que nao passa pela conta de credito). */
  next_billing_date?: string
  purchased_at?: string
  attached_at?: string
}

export interface PublicIPCustomerListResponse {
  total: number
  items: PublicIPCustomer[]
}

export interface PublicIPAllocated {
  id: string
  ip_address: string
  gateway: string
  netmask: string
  dns_servers?: string[]
  cluster_id: string
}

// ===== CP: Pool Management =====

export const publicIpApi = {
  // CP: Add IP range to pool
  async addIPRange(
    poolId: string,
    data: PublicIPRangeCreateRequest,
  ): Promise<PublicIPRangeCreateResponse> {
    return await apiClient.post(`/api/v1/network-pools/${poolId}/public-ips`, data)
  },

  // CP: List IPs in pool
  async listPoolIPs(poolId: string): Promise<PublicIPListResponse> {
    return await apiClient.get(`/api/v1/network-pools/${poolId}/public-ips`)
  },

  // CP: a vida do endereço em CICLOS de posse (#1067) — quem teve, quando, e o que houve.
  // Pagina por CICLO, nunca por evento: paginar evento partiria um ciclo entre páginas.
  // A resposta já nasce recortada pelo escopo de quem pergunta; a tela não filtra nada.
  async getPoolIPHistory(
    poolId: string,
    ipId: string,
    params: { skip?: number; limit?: number } = {},
  ): Promise<PublicIPHistoryResponse> {
    return await apiClient.get(`/api/v1/network-pools/${poolId}/public-ips/${ipId}/history`, {
      params,
    })
  },

  // CP: os endereços que SAÍRAM do pool e não voltaram (#1071). Paginado por endereço; o
  // `id` de cada item abre o histórico pelo MESMO `getPoolIPHistory`.
  async listRemovedPoolIPs(
    poolId: string,
    params: { skip?: number; limit?: number } = {},
  ): Promise<PublicIPRemovedListResponse> {
    return await apiClient.get(`/api/v1/network-pools/${poolId}/public-ips/removed`, { params })
  },

  // CP: Delete IP from pool (só `available`; tira o endereço do pool)
  async deletePoolIP(poolId: string, ipId: string): Promise<void> {
    await apiClient.delete(`/api/v1/network-pools/${poolId}/public-ips/${ipId}`)
  },

  // ===== CP: liberar e dedicar (#1059) — exigem network:manage =====

  // CP: o que a liberação vai fazer (dono, revenda, uso, ESTORNO) e o que a impede — sem escrever
  async previewReleasePoolIP(poolId: string, ipId: string): Promise<PublicIPReleasePreview> {
    return await apiClient.post(
      `/api/v1/network-pools/${poolId}/public-ips/${ipId}/release/preview`,
    )
  },

  // CP: libera a alocação — o IP volta a `available` e CONTINUA no pool (não é o delete).
  // `data.expected_owner_organization_id` é o dono VISTO NA PRÉVIA: vai como prova sob o
  // lock, e divergência (o cliente liberou e outro comprou nesse meio-tempo) é 409.
  async releasePoolIP(
    poolId: string,
    ipId: string,
    data: PublicIPReleaseRequest = {},
  ): Promise<PublicIPReleaseResponse> {
    return await apiClient.post(`/api/v1/network-pools/${poolId}/public-ips/${ipId}/release`, data)
  },

  // CP: revendas com seus clientes cativos e os produtos de IPv4 — para o modal de dedicação
  async getAllocationTargets(poolId: string): Promise<PublicIPAllocationTargets> {
    return await apiClient.get(`/api/v1/network-pools/${poolId}/public-ips/allocation-targets`)
  },

  // CP: a dedicação sem escrever — cliente, revenda resolvida e o preço (ou a cortesia)
  async previewAllocatePoolIP(
    poolId: string,
    ipId: string,
    data: PublicIPAllocateRequest,
  ): Promise<PublicIPAllocatePreview> {
    return await apiClient.post(
      `/api/v1/network-pools/${poolId}/public-ips/${ipId}/allocate/preview`,
      data,
    )
  },

  // CP: dedica o IP a um cliente final, com a cobrança explícita no corpo
  async allocatePoolIP(
    poolId: string,
    ipId: string,
    data: PublicIPAllocateRequest,
  ): Promise<PublicIPAllocateResponse> {
    return await apiClient.post(`/api/v1/network-pools/${poolId}/public-ips/${ipId}/allocate`, data)
  },

  // ===== EC: Purchase & Management =====

  // EC: Count available IPs (lightweight stock check)
  async countAvailable(): Promise<{ available: number }> {
    return await apiClient.get('/api/v1/end-customer/public-ips/available/count')
  },

  // EC: List available IPs for purchase
  async listAvailable(clusterId?: string): Promise<PublicIPAvailableListResponse> {
    const params = clusterId ? { cluster_id: clusterId } : {}
    return await apiClient.get('/api/v1/end-customer/public-ips/available', { params })
  },

  // EC: Purchase an IP
  async purchase(data: PublicIPPurchaseRequest): Promise<PublicIPPurchaseResponse> {
    return await apiClient.post('/api/v1/end-customer/public-ips/purchase', data)
  },

  // EC: List my IPs
  async listMyIPs(statusFilter?: string): Promise<PublicIPCustomerListResponse> {
    const params = statusFilter ? { status_filter: statusFilter } : {}
    return await apiClient.get('/api/v1/end-customer/public-ips', { params })
  },

  // EC: Release IP
  async releaseIP(ipId: string): Promise<{ message: string; ip_id: string; status: string }> {
    return await apiClient.post(`/api/v1/end-customer/public-ips/${ipId}/release`)
  },

  // EC: List allocated (unattached) IPs for VM creation
  async listAllocated(clusterId?: string): Promise<{ total: number; items: PublicIPAllocated[] }> {
    const params = clusterId ? { cluster_id: clusterId } : {}
    return await apiClient.get('/api/v1/end-customer/public-ips/allocated', { params })
  },
}

// ===== Helpers =====

export const getPublicIPStatusLabel = (status: string): string => {
  switch (status) {
    case 'available':
      return 'Disponível'
    case 'allocated':
      return 'Alocado'
    case 'attached':
      return 'Em uso'
    case 'released':
      return 'Liberado'
    default:
      return status
  }
}

export const getPublicIPStatusColor = (status: string): string => {
  switch (status) {
    case 'available':
      return 'text-green-600 bg-green-50'
    case 'allocated':
      return 'text-blue-600 bg-blue-50'
    case 'attached':
      return 'text-purple-600 bg-purple-50'
    case 'released':
      return 'text-gray-600 bg-gray-50'
    default:
      return 'text-gray-600 bg-gray-50'
  }
}
